import React, { useState } from "react";

interface Source {
  title: string;
  url?: string;
}

interface SourcesCitationProps {
  sources: Source[];
}

const LinkIcon = () => (
  <svg
    width="14"
    height="14"
    viewBox="0 0 14 14"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
    style={{ flexShrink: 0 }}
  >
    <path
      d="M5.833 7.583a2.917 2.917 0 004.084.467l1.75-1.75a2.917 2.917 0 00-4.125-4.125L6.567 3.15"
      stroke="#6366F1"
      strokeWidth="1.2"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
    <path
      d="M8.167 6.417a2.917 2.917 0 00-4.084-.467l-1.75 1.75a2.917 2.917 0 004.125 4.125l.975-.975"
      stroke="#6366F1"
      strokeWidth="1.2"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

const DocumentIcon = () => (
  <svg
    width="14"
    height="14"
    viewBox="0 0 14 14"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
    style={{ flexShrink: 0 }}
  >
    <path
      d="M8.167 1.167H3.5A1.167 1.167 0 002.333 2.333v9.334A1.167 1.167 0 003.5 12.833h7A1.167 1.167 0 0011.667 11.667V4.667L8.167 1.167z"
      stroke="#6366F1"
      strokeWidth="1.2"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

const ChevronIcon: React.FC<{ expanded: boolean }> = ({ expanded }) => (
  <svg
    width="14"
    height="14"
    viewBox="0 0 14 14"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
    style={{
      flexShrink: 0,
      transform: expanded ? "rotate(180deg)" : "rotate(0deg)",
      transition: "transform 0.2s",
    }}
  >
    <path
      d="M3.5 5.25L7 8.75L10.5 5.25"
      stroke="#6B7280"
      strokeWidth="1.2"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

const SourcesCitation: React.FC<SourcesCitationProps> = ({ sources }) => {
  const [expanded, setExpanded] = useState(false);

  if (!sources || sources.length === 0) return null;

  return (
    <div style={styles.container}>
      <button type="button" style={styles.header} onClick={() => setExpanded(!expanded)}>
        <DocumentIcon />
        <span style={styles.headerText}>Sources</span>
        <ChevronIcon expanded={expanded} />
      </button>
      {expanded && (
        <div style={styles.sourceList}>
          {sources.map((source, index) => (
            <div key={index} style={styles.sourceItem}>
              {source.url ? (
                <>
                  <LinkIcon />
                  <a href={source.url} target="_blank" rel="noopener noreferrer" style={styles.sourceLink}>
                    {source.title || source.url}
                  </a>
                </>
              ) : (
                <>
                  <DocumentIcon />
                  <span style={styles.sourceText}>{source.title}</span>
                </>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

const styles: Record<string, React.CSSProperties> = {
  container: {
    marginTop: "2px",
    borderRadius: "8px",
  },
  header: {
    display: "flex",
    alignItems: "center",
    gap: "6px",
    padding: "2px 0",
    cursor: "pointer",
    background: "transparent",
    border: "none",
    fontFamily: "inherit",
    width: "100%",
  },
  headerText: {
    fontSize: "13px",
    fontWeight: 500,
    color: "#374151",
    flex: 1,
    textAlign: "left",
  },
  sourceList: {
    display: "flex",
    flexDirection: "column",
    gap: "4px",
    paddingBottom: "4px",
  },
  sourceItem: {
    display: "flex",
    alignItems: "center",
    gap: "8px",
    padding: "6px 0",
  },
  sourceLink: {
    fontSize: "13px",
    color: "#6366F1",
    textDecoration: "none",
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap" as const,
  },
  sourceText: {
    fontSize: "13px",
    color: "#374151",
  },
};

export default SourcesCitation;
